import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useProductStore } from '../stores/ProductStore';
import PageLayout from '../components/PageLayout';
import { Modal, Box, Fade, Typography, useTheme } from '@mui/material';
import VideoBanner from '../components/VideoBanner';
import Banner from '../components/Banner';
import { YouTubePlayer } from './news/MarkdownRenderer';
import { useCampaign } from '../contexts/CampaignContext';
import { useExperiment } from '../hooks/useExperiment';

// Hero CTA copy under A/B test. Falls back to the default carousel headline when
// the experiment isn't active (variant === null).
const HERO_CTA_VARIANTS = {
  a: 'Back us on Kickstarter',
  b: 'Hear the difference',
};

const LandingPage = observer(() => {
  const productStore = useProductStore();
  const { popup } = useCampaign();
  const heroCtaVariant = useExperiment('hero-cta');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    productStore.loadProducts();
  }, [productStore]);

  useEffect(() => {
    // Only schedule the popup when the resolve API returned one for this page.
    if (!popup) return undefined;

    const storageKey = 'campaignPopup:' + popup.id;
    const frequency = popup.frequency || 'session';

    // 'once' persists forever (localStorage); 'session' resets per tab session
    // (sessionStorage); 'always' shows every visit and stores nothing.
    if (frequency === 'once' && window.localStorage.getItem(storageKey)) return undefined;
    if (frequency === 'session' && window.sessionStorage.getItem(storageKey)) return undefined;

    const delayMs = (popup.delaySeconds || 0) * 1000;
    const timer = setTimeout(() => {
      setIsModalOpen(true);
      if (frequency === 'once') {
        window.localStorage.setItem(storageKey, 'true');
      } else if (frequency === 'session') {
        window.sessionStorage.setItem(storageKey, 'true');
      }
    }, delayMs);
    return () => clearTimeout(timer);
  }, [popup]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const carouselSections = [
    {
      // Most prominent hero headline — swapped by the `hero-cta` experiment when
      // active, otherwise the default copy below.
      text: HERO_CTA_VARIANTS[heroCtaVariant] || "Ear Physics: Superior Sound, Personalized Hearing",
      subheader: "Experience the future of listening with our Adaptive Response IEMs.",
    },
    {
      text: "Coming Soon to Kickstarter",
      subheader: "Support Ear Physics and be among the first to hear the difference.",
    },
    {
      text: "Adaptive Response IEMs",
      subheader: "Personalized sound, engineered for musicians and audiophiles.",
    },
    {
      text: "Hear Your Music Like Never Before",
      subheader: "Clean, immersive audio that adapts to you.",
    },
    {
      text: "High Fidelity On the Go",
      subheader: "Take studio-quality sound wherever life takes you.",
    },
  ];

  const contentSections = [
    {
      title: "Ear Physics: Superior Sound, Personalized Hearing",
      subheader: "Experience unmatched clarity and personalized audio.",
      content: "Our In-Ear Monitors are designed for musicians, audiophiles, and anyone seeking an exceptional listening experience. Feel every detail with advanced audio technology, whether on stage, in the studio, or on the go.",
      image: productStore.products[0]?.primaryImage?.url,
    },
    {
      title: "Personalized Sound with Adaptive Audio",
      subheader: "Tuned to your ears",
      content: "A guided hearing test in the Ear Physics app, paired with our patented Adaptive Audio technology, delivers customized sound correction tailored for music — so every track sounds the way it should, for you.",
      image: productStore.products[1]?.primaryImage?.url,
    },
    {
      title: "Get the Sound You Deserve",
      subheader: "Adaptive Response IEMs: Made for Musicians",
      content: "Our Adaptive Response IEMs give you a clean, personalized sound that lets you catch every detail. No more ear fatigue—just hours of listening comfort that adapts to you.",
      image: productStore.products[2]?.primaryImage?.url,
    },
    {
      title: "Support Us on Kickstarter",
      subheader: "Superior audio, personalized to you.",
      content: "Ear Physics is launching on Kickstarter. Back us to get high-fidelity IEMs with adaptive sound technology for a comfortable, immersive listening experience — music the way it was meant to be heard.",
      image: productStore.products[3]?.primaryImage?.url,
    },
    {
      title: "Where Technology Meets Real Sound",
      subheader: "Next-Gen Sound Tech",
      content: "Step into a new standard of sound with our Adaptive Response IEMs. They adjust to your ear's unique shape, giving you a listening experience that’s all about you.",
      image: productStore.products[4]?.primaryImage?.url,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselSections.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [carouselSections.length]);

  return (
    <PageLayout pageTitle="Ear Physics - Where High-Fidelity Audio Meets Hearing Enhancement" sections={contentSections} navbarTransparent={true}>
      {carouselSections.length > 0 && (
        <VideoBanner
          videoId="1023423745"
          title={carouselSections[currentIndex].text}
          subheader={carouselSections[currentIndex].subheader}
          scrollTargetId="main-content"
        />
      )}
      <Box
        sx={{
          width: '100%',
          bgcolor: theme.palette.background.default,
          py: { xs: 6, sm: 8, md: 10 },
          px: { xs: 2, md: 3 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            mb: { xs: 3, sm: 4 },
            fontWeight: 'bold',
            letterSpacing: '2px',
          }}
        >
          Meet Ear Physics
        </Typography>
        <Box sx={{ maxWidth: 960, mx: 'auto', mb: { xs: 8, sm: 10 } }}>
          <YouTubePlayer
            videoId="2RnTFhR1u9s"
            title="Meet Ear Physics"
            originalUrl="https://www.youtube.com/watch?v=2RnTFhR1u9s"
          />
        </Box>
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            mb: { xs: 3, sm: 4 },
            fontWeight: 'bold',
            letterSpacing: '2px',
          }}
        >
          See Ear Physics in Action
        </Typography>
        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
          <YouTubePlayer
            videoId="HgdJjStSFds"
            title="Ear Physics in Action"
            originalUrl="https://www.youtube.com/watch?v=HgdJjStSFds"
          />
        </Box>
      </Box>
      {popup && (
        <Modal
          open={isModalOpen}
          onClose={handleCloseModal}
          closeAfterTransition
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Fade in={isModalOpen}>
            <Box sx={{ width: '100%', maxWidth: 600, px: 2 }}>
              <Banner
                title={popup.title}
                subtitle={popup.body}
                buttonText={popup.ctaUrl ? (popup.ctaLabel || 'Learn more') : undefined}
                buttonLink={popup.ctaUrl || undefined}
                imageSrc={productStore.products[0]?.primaryImage?.url}
                imagePosition="left"
              />
            </Box>
          </Fade>
        </Modal>
      )}
    </PageLayout>
  );
});

export default LandingPage;
